import { DEBUG } from 'config';
import { has, deburr, orderBy, each, filter, find, findIndex, get, isArray, map, merge, slice, isEmpty } from 'lodash';
import { defineStore } from 'pinia';
import ApiHelper from 'plugins/api';
import { tr } from 'plugins/i18n';
import useRouter from 'plugins/router';
import { pinia } from 'plugins/store/index';
import { computed, readonly, ref, toRaw } from 'vue';

type API_ITEM = {
  url?: string,
  loaded: boolean
}

interface Organisationstore extends Obj {
  deepLinked: boolean,
  page?: PageOrganisations
  items: OrganisationsItem[]
  apis: {
    page: API_ITEM
    list: API_ITEM
    details: API_ITEM
  },
  // slug of current news - set/unset in single news page
  slug?:string,
  pagination: {
    page: number,
    perPage: number,
    max: number,
  },
  options: {
    ordering: {
      label: string,
      value: string,
      field: any,
      order: 'asc' | 'desc',
    }[],
  },
  filters: {
    place?: string[],
    category?: string[],
    activity_sectors?: string[],
    ordering?: string,
  },
}
type FiltersKey = keyof Organisationstore["options"];

const useStore = defineStore('Organisations', () => {
  const state = ref<Organisationstore>({
    loaded: false,
    deepLinked: true,
    apis: {
      page: {
        url: undefined,
        loaded: false,
      },
      list: {
        url: 'organizations',
        loaded: false,
      },
      details: {
        url: undefined,
        loaded: false,
      }
    },
    page: undefined,
    items: [],
    slug:undefined,
    pagination: {
      page: 1,
      perPage: 12,
      max: 1,
    },
    options: {
      // for ordering purpose only -> custom behavior on this.
      ordering: [
        {
          label: tr('PAGE_ORGANISATIONS.ordering.titleasc'),
          value: 'titleasc',
          field: 'title',
          order: 'asc',
        },
        {
          label: tr('PAGE_ORGANISATIONS.ordering.titledesc'),
          value: 'titledesc',
          field: 'title',
          order: 'desc',
        },
      ],
    },
    filters: {
      place: undefined,
      category: undefined,
      activity_sectors: undefined,
      ordering: undefined,
    },
  })

  /* keep an hystory of visited organisations
      so can weight to show less/un-visited ones in related
      */
  const history = ref<Record<string, number>>({}) // {url:visitedCount, otherUrl:counter...}

  // * ------------------------------------------------------------------------- Getters / Setters
  
  function getItemBySlug(slug: string){
    return find(state.value.items, (item) => item.slug === slug)
  }
  function getItemById(id: number){
    return find(state.value.items, (item) => item.id === id)
  }
  /** set current organisations (used for related) */
  function setCurrent(slug:string){
    state.value.slug = slug
    history.value[slug] = (history.value[slug] ?? 0) + 1
  }
  /** safe unset current organisations */
  function unsetCurrent(slug:string){
    // if different... unexpected but may meen that an other one is already defined
    if(state.value.slug != slug) return
    state.value.slug = undefined
  }

  /** 
   * * ------------------------------------------------------------------------- LOADING
   **/
  const loadPage = async (url: string, loadTwigData = false) => {
    const api = state.value.apis.page;

    // if first page load global && twig variable is available.
    if (loadTwigData && !api.loaded && window.__PAGE) {
      setPage(window.__PAGE as PageOrganisations);
      api.loaded = true;
      return Promise.resolve(window.__PAGE)
    }

    // default behavior
    const request = api.loaded
      ? Promise.resolve(state.value.page)
      : ApiHelper.get(url, {})
        .then(({ data }: any) => {
          return data as PageOrganisations
        })
        .then(setPage)
        .then(data => { 
          api.loaded = true
          return data
        })
    return request
  }

  const loadList = async (loadTwigData = false) => {
    const api = state.value.apis.list;

    // if first page load global && twig variable is available.
    if (loadTwigData && !api.loaded && window.__LISTING) {
      setItems(window.__LISTING as OrganisationsItem[]);
      api.loaded = true;
      return Promise.resolve(window.__LISTING)
    }

    // default behavior
    const request = api.loaded
      ? Promise.resolve(state.value.items).then(() => {
        setDefaultFilters();
      })
      : ApiHelper.get(api?.url || '')
        .then(({ data }: any) => {
          return map(data, ({ ...item }: any) => ({
            ...item,
          }))
        })
        .then(setItems)
        .then(data => {
          api.loaded = true
          return data
        })

    return request
      .then(data => {
        return data
      })
  }

  const loadDetails = async (url:string, slug: string, loadTwigData = false) => {
    const api = state.value.apis.details;
    
    // if first page load global && twig variable is available.
    if (loadTwigData && !api.loaded && window.__PAGE) {
      setPage(window.__PAGE as PageOrganisation);
      api.loaded = true;
      return Promise.resolve(window.__PAGE)
    }

    return new Promise((resolve, reject)=>{
      const entry = getItemBySlug(slug)
      if (entry && ({}).hasOwnProperty.call(entry, 'loaded') && entry.loaded) {
        return resolve(entry)
      }
      reject()
    })
    .catch(()=>{
      const api = { ...state.value.apis.details }
      api.url = url
  
      return ApiHelper.get(api.url, {}, {showErrorMessage: false})
        .then(({ data }: any) => data as OrganisationsItem)
        .then((data) => {
          return {
            ...data,
            loaded: true,
          }
        })
        .then(mergeItem)
    })
  }

  /** 
   * * ------------------------------------------------------------------------- FILTERS
   **/

  // Filtering here.
  const filtered = computed(() => {
    let rawItems = toRaw(state.value.items);

    // feel free to add any other filtering options, like date range or year
    each(state.value.filters, (value?:string|string[], key?:string) => {
      if (key && key !== 'ordering' && key !== 'dates' && value) {
        rawItems = filter(rawItems, (item) => {
          const itemData = get(item, key);
          // place is super special 
          if (key === 'place' && item.address) {
            // @ts-ignore
            return value.indexOf(item.address?.region?.id) > -1
          }
          // if value is a string
          else if (typeof itemData === 'string') {
            return itemData == value ? true : false
            // if value is a Category
          } else if (itemData?.id) {
            return value.indexOf(itemData.id) > -1
            // return itemData.id == value ? true : false
          // when there is multiple category (inside array)
          } else if (isArray(itemData)) {
            // console.log(itemData, value)
            // return find(itemData, { 'id': value }) ? true : false
            return find(itemData, (i) => {
              // console.log(i, value, value.indexOf(i.id))
              return value.indexOf(i.id) > -1
            }) ? true : false
          }
          return false;
        })
      }
    })

    if (state.value.filters.ordering) {
      const orderOption = find(state.value.options.ordering, ['value', state.value.filters.ordering])
      if (orderOption) {
        rawItems = orderBy(rawItems, (item:any) => { 
          return deburr(get(item, orderOption.field)?.toString() || '')}
        , orderOption.order);
      }
    }

    return rawItems
  });

  // setFilter
  function setFilter(key:FiltersKey, value?:any){
    // on filter change reset to page #1
    // setPagination(1);

    // @ts-ignore
    state.value.filters[key] = value || undefined;

    return state.value.filters;
  }

  function resetFilter() {
    state.value.filters.place = undefined;
    state.value.filters.category = undefined;
    state.value.filters.activity_sectors = undefined;
    setPagination(1);
  }

  function setDefaultFilters(){
    const routeQuery = useRouter().currentRoute.value.query
    if(isEmpty(routeQuery)) {
      return resetFilter();
    }
    
    if (routeQuery.ordering) {
      // @ts-ignore
      state.value.filters.ordering = routeQuery.ordering
      delete routeQuery.ordering;
    }
    if (routeQuery.page) {
      state.value.pagination.page = parseInt(routeQuery.page as string, 10) || 1;
    }
    delete routeQuery.page;

    each(routeQuery, (value:any, key:any) => {  
      if (has(state.value.filters, key)) {
        const cleanValue = map(isArray(value) ? value : [value], (val) => {
          return parseInt(val, 10) || val;
        });
        // @ts-ignore
        state.value.filters[key as FiltersKey] = cleanValue;
      }
    })
  }


  // * ------------------------------------------------------------------------- PAGINATION
  
  // const cards = computed(() => state.value.items)
  const paginated = computed(() => {
    const items = filtered.value;
    const { page, perPage } = state.value.pagination;
    const pageStart = (page - 1) * perPage;
    const pageEnd = pageStart + perPage;
    const paginated = slice(items, pageStart, pageEnd);

    // recompute max page from current filtered items
    state.value.pagination.max = Math.ceil(items.length / state.value.pagination.perPage)

    if (state.value.deepLinked) {
      syncRouterQuery()
    }

    return paginated;
  })


  function setPagination(page: number){
    state.value.pagination.page = page;
  }
  
  function prevPagination(){
    if (state.value.pagination.page - 1 === 0) {
      state.value.pagination.page = 1
    } else {
      state.value.pagination.page = state.value.pagination.page - 1;
    }
  }

  function nextPagination(){
    if (state.value.pagination.page + 1 > state.value.pagination.max) {
      state.value.pagination.page = state.value.pagination.max
    } else {
      state.value.pagination.page = state.value.pagination.page + 1;
    }
  }

  // * ------------------------------------------------------------------------- INTERNAL

  /** simply store page data in state... */
  function setPage(page: PageOrganisations){
    state.value.page = page
    return page
  }

  /** store items in state */
  function setItems(items: OrganisationsItem[]){
    // ensure if item before
    const previous = [...state.value.items]

    state.value.items = items
    
    if (previous.length > 0) {
      each(previous, mergeItem)
    }

    // createFilters();
    setDefaultFilters();

    state.value.pagination.max = Math.ceil(items.length / state.value.pagination.perPage)
    return state.value.items
  }

  /** update/merge item in state
   * like state have many simple items - then load detail one - so merge it with current state/list
   */
  function mergeItem(item: OrganisationsItem){
    if (!item) return
    if (state.value.items.length === 0) {
      setItems([item])
      return item
    }
    const findex = findIndex(state.value.items, ({ id }) => id === item.id)
    if(findex < 0) return
    state.value.items[findex] = merge({}, state.value.items[findex], item)
    return state.value.items[findex]
  }

  /** sync router params with search/pagination etc */
  function syncRouterQuery(){
    const query:any = {};
    each(state.value.filters, (value:any, key:any) => {
      if (value && key) {
        query[key] = value;
      }
    });

    if (state.value.pagination.page && state.value.pagination.page !== 1) {
      query.page = state.value.pagination.page;
    }

    useRouter().replace({query: query})
  }

  return {
    state: readonly(state),

    // * LOAD
    loadPage,
    loadList,
    loadDetails,

    // * getters
    getItemBySlug,
    getItemById,

    // * related
    setCurrent, // visited news detail
    unsetCurrent, // visited news detail
    //getRelated,

    // * filters
    // filtered data without pagination..
    filtered,
    // active filters
    filters: computed(() => state.value.filters),
    // get filters options
    options: computed(() => state.value.options),
    setFilter,
    resetFilter,

    // * pagination
    setPagination,
    prevPagination,
    nextPagination,
    // real data.
    paginated,
    pagination: computed(() => state.value.pagination),
  }
})
export const store = useStore(pinia);
export default store;

// @ts-ignore
if(DEBUG) window.storeOrganisations = store
